import * as yup from 'yup';
import { eDepositCategory } from '../../types/order/escrow/eDepositCategory';
import { eAchPullPayerType } from '../../types/order/escrow/eAchPullPayerType';
import { eEscrowWireDisbursementCategories } from '../../components/order/escrow/disbursements/achPushDisbursements/createWireFromACH/enums/eEscrowWireDisbursementCategories';
import { IAchAccountData } from '../../types/accounting/data/IEscrowExpectedIncomingAchData';
import { eACHDisbursementCategories } from '../../types/order/escrow/eEscrowAchPushDisbursementCategories';
import { eManualCheckDisbursementCategory } from '../../types/accounting/eManualCheckDisbursementCategory';

const alphaNumericRE: RegExp = /^[a-zA-Z\d]+$/;
const alphaNumericSpaceUnderscoreAndDashAndSlashRE: RegExp = /^[a-zA-Z0-9,/_\- ]+$/;

export const createEditIncomingTransactionsValidationSchema = yup.object({
  // incomings
  achPullDeposits: yup.array(
    yup.object({
      amountCents: yup.number().positive().required(),
      category: yup.mixed<eDepositCategory>().required(),
      wellsFargoTransactionNumber: yup
        .string()
        .matches(alphaNumericRE, 'Only numbers and letters are valid inputs')
        .required()
        .typeError('This is required'),
      payerType: yup.mixed<eAchPullPayerType>().required(),
      ownerId: yup.string().nullable(),
      account: yup
        .object()
        .optional()
        .nullable()
        .default(undefined)
        .shape({
          name: yup.string().required().nullable(false).typeError('This is required'),
          routingNumber: yup
            .string()
            .required()
            .test(
              'lengthCheck',
              'Routing number is the wrong length (should be 9 characters)',
              (rtn) => rtn?.length === 9
            )
            .typeError('This is required'),
          accountNumber: yup
            .string()
            .min(5, 'Account number is too short (minimum is 5 characters)')
            .required()
            .typeError('This is required'),
          accountType: yup.mixed<IAchAccountData>().required(),
        }),
    })
  ),
  wireDeposits: yup.array(
    yup.object({
      amountCents: yup.number().typeError('Amount must be a number').positive().required(),
      payerType: yup.string().required(),
      category: yup.string().required(),
    })
  ),
  checkDeposits: yup.array(
    yup.object({
      amountCents: yup.number().typeError('Amount must be a number').positive().required(),
      payerType: yup.string().required(),
      category: yup.string().required(),
    })
  ),
  // outgoings
  wireDisbursements: yup.array(
    yup.object({
      amountCents: yup
        .number()
        .typeError('Amount must be a number')
        .positive()
        .required('Amount must be greater than 0'),
      beneficiary: yup.string().required(),
      category: yup.mixed<eEscrowWireDisbursementCategories>().required('Category is not included in the list'),
      memo: yup
        .string()
        .max(160, 'Memo must be less than 160 characters')
        .matches(alphaNumericSpaceUnderscoreAndDashAndSlashRE, 'Invalid characters')
        .notRequired()
        .nullable(),
      routingNumber: yup
        .string()
        .required()
        .test('lengthCheck', 'Routing number is the wrong length (should be 9 characters)', (rtn) => rtn?.length === 9)
        .typeError('This is required'),
      accountNumber: yup.string().min(5, 'Account number is too short (minimum is 5 characters)').required(),
    })
  ),
  achPushDisbursements: yup.array().of(
    yup.object().shape({
      account: yup
        .object()
        .optional()
        .nullable()
        .default(undefined)
        .shape({
          name: yup.string().required().nullable(false).typeError('This is required'),
          routingNumber: yup
            .string()
            .required()
            .test(
              'lengthCheck',
              'Routing number is the wrong length (should be 9 characters)',
              (rtn) => rtn?.length === 9
            )
            .typeError('This is required'),
          accountNumber: yup
            .string()
            .min(5, 'Account number is too short (minimum is 5 characters)')
            .required()
            .typeError('This is required'),
          accountType: yup.mixed<IAchAccountData>().required(),
        }),
      amountCents: yup.number().typeError('Amount must be a number').positive().required(),
      category: yup.mixed<eACHDisbursementCategories>().required('Category is not included in the list'),
      wellsFargoTransactionNumber: yup
        .string()
        .matches(alphaNumericRE, 'Only numbers and letters are valid inputs')
        .nullable(),
    })
  ),
  manualCheckDisbursements: yup.array(
    yup.object({
      amountCents: yup.number().typeError('Amount must be a number').positive().required(),
      name: yup.string().required().typeError('This is required'),
      mailingAddress: yup.object().shape({
        streetAddress: yup.string().required().typeError('This is required'),
        city: yup.string().required().typeError('This is required'),
        stateAbbreviation: yup.string().required().typeError('This is required'),
        zipCode: yup.string().required().typeError('This is required'),
      }),
      memo: yup
        .string()
        .matches(alphaNumericSpaceUnderscoreAndDashAndSlashRE, 'Invalid characters')
        .required('Memo field cannot be null')
        .nullable(),
      category: yup.mixed<eManualCheckDisbursementCategory>().required(),
    })
  ),
  restrictedInternalTransfers: yup.array(
    yup.object({
      amountCents: yup.number().typeError('Amount must be a number').positive().required(),
      category: yup.string().nullable(),
      destinationAccount: yup.object().shape(
        {
          id: yup.string().when('name', {
            is: (name: any) => !name,
            then: yup.string().required("Destination Account can't be blank"),
          }),
          name: yup.string().when('id', {
            is: (id: any) => !id,
            then: yup.string().required("Destination Account can't be blank"),
          }),
        },
        [['id', 'name']]
      ),
    })
  ),
  unrestrictedInternalTransfers: yup.array(),
});
